//changed due to package upgrade
import React, { useEffect, useMemo, useState } from 'react';
import { alpha, Box, Button, Grid, Typography, Tabs, Tab } from '@mui/material';
import jwtAxios from '@projectK/services/auth/JWT';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import AppInfoView from '@projectK/components/AppInfoView';
import { Fonts } from '@projectK/constants/AppEnums';
import IntlMessages from '@projectK/helpers/IntlMessages';
import PropTypes from 'prop-types';
import { hasPermission, useRolePermissions } from '@projectK/context/RolesProvider';
import {
  ProfileFieldInput,
  validateProfileFieldValueClient,
  serializeProfileFieldValueForApi,
  mapMemberProfileDataToFormValues,
  memberProfileFieldKey,
  getProfileFieldGridSize,
  getProfileFieldsStructureKey,
  validateMemberProfileFields,
  deserializeProfileFieldValueForForm,
} from '@projectK/modules/profileFields';

const ProfileData = ({ profileFields, companyTheme }) => {
  const [formValues, setFormValues] = useState({});
  const [initialValues, setInitialValues] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const infoViewActionsContext = useInfoViewActionsContext();
  const { user } = useAuthUser();
  const { rolePermissions } = useRolePermissions();
  const cartUserIds = Array.isArray(user?.id) ? user?.id : [user?.id];

  const fetchUsersData = async () => {
    try {
      const responses = await Promise.all(
        cartUserIds?.map((userId) =>
          jwtAxios.get(`/user-profiles-data/${userId}`),
        ),
      );
      const allData = responses?.flatMap((response) => response.data);
      const initial = mapMemberProfileDataToFormValues(allData, profileFields);
      setFormValues(initial);
      setInitialValues(initial);
    } catch (error) {
      console.error('error', error);
    }
  };

  const profileFieldsKey = useMemo(
    () => getProfileFieldsStructureKey(profileFields),
    [profileFields],
  );

  useEffect(() => {
    if (!profileFieldsKey || cartUserIds.length === 0) {
      return undefined;
    }

    let cancelled = false;

    const loadFormValues = async () => {
      try {
        const responses = await Promise.all(
          cartUserIds.map((userId) =>
            jwtAxios.get(`/user-profiles-data/${userId}`),
          ),
        );
        if (cancelled) {
          return;
        }
        const allData = responses.flatMap((response) => response.data);
        const initial = mapMemberProfileDataToFormValues(
          allData,
          profileFields,
        );
        setFormValues(initial);
        setInitialValues(initial);
      } catch (error) {
        console.error('error', error);
      }
    };

    loadFormValues();

    return () => {
      cancelled = true;
    };
  }, [cartUserIds.length, profileFieldsKey, profileFields]);

  const handleProfileFieldChange = (userId) => (field, valueOrEvent) => {
    const value =
      valueOrEvent?.target !== undefined
        ? valueOrEvent.target.value
        : valueOrEvent;
    const name = memberProfileFieldKey(userId, field);
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const groupedFields = profileFields.reduce((acc, field) => {
    const { profile_name, product_id } = field;
    const key = `${profile_name}_${product_id}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(field);
    return acc;
  }, {});

  const tabLabels = Object.keys(groupedFields);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);

    const errors = validateMemberProfileFields(
      Object.values(groupedFields),
      cartUserIds,
      formValues,
    );

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      for (const userId of cartUserIds) {
        const userFields = profileFields.map((group) =>
          group.profile_fields.map((field) => ({
            profile_field_id: field?.profile_field_id,
            value: serializeProfileFieldValueForApi(
              field,
              deserializeProfileFieldValueForForm(
                field,
                formValues[memberProfileFieldKey(userId, field)],
              ),
            ),
            profile_id: field.profile_id,
            product_id: field.product_id,
          })),
        );

        if (userFields.length > 0) {
          const promises = userFields.map(async (data) => {
            try {
              const profileGroups = {
                user_id: userId,
                product_id: data[0]?.product_id,
                profile_id: data[0]?.profile_id,
                fields: data,
              };

              const profileResponse = await jwtAxios.post('/user-profiles', {
                user_id: profileGroups.user_id,
                product_id: profileGroups.product_id,
                profile_id: profileGroups.profile_id,
              });

              const userProfileId = profileResponse?.data?.id;

              if (userProfileId) {
                const profilesDataResponse = await jwtAxios.post(
                  '/user-profiles-data',
                  {
                    user_profile_id: userProfileId,
                    fields: profileGroups.fields,
                  },
                );

                if (profilesDataResponse.status === 201) {
                  return 'Profile data added successfully!';
                }
                throw new Error('Failed to add profile data');
              }
              throw new Error('Failed to create user profile');
            } catch (error) {
              console.error('Error processing profile data:', error);
              return error.message;
            }
          });

          const results = await Promise.all(promises);
          if (
            results.every(
              (result) => result === 'Profile data added successfully!',
            )
          ) {
            setFormSubmitted(false);
            infoViewActionsContext.showMessage(
              'All profiles added successfully!',
            );
          }
        }
      }
      fetchUsersData();
    } catch (error) {
      infoViewActionsContext.fetchError(error?.response?.data?.error);
    }
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setFormSubmitted(false);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  return (
    <>
      <Box className='item-hover'>
        <Typography
          component='h3'
          sx={{
            paddingBottom: 5,
            fontSize: 16,
            fontWeight: Fonts.BOLD,
            color: companyTheme ? companyTheme.primary.main : 'text.primary',
          }}
        >
          <IntlMessages id='common.ProfileFieldData' />
        </Typography>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          className='profileinfo_tabs'
        >
          {tabLabels.map((label, index) => {
            const updatedLabel = label.split('_')[0];
            return (
              <Tab
                label={updatedLabel}
                key={index + 0}
                sx={{ textTransform: 'none' }}
              />
            );
          })}
        </Tabs>
        <form onSubmit={handleSubmit}>
          {profileFields?.length ? (
            cartUserIds.map((userId, cartIndex) => (
              <Box key={userId}>
                {Object.entries(groupedFields).map(
                  ([, fields], groupIndex) =>
                    selectedTab === groupIndex && (
                      <Box
                        key={groupIndex + 0}
                        sx={{ marginBottom: 5, marginTop: 5 }}
                      >
                        <Grid container spacing={2}>
                          {fields[0]?.profile_fields?.map((field, index) => {
                            const key = memberProfileFieldKey(userId, field);
                            const displayValue =
                              deserializeProfileFieldValueForForm(
                                field,
                                formValues[key],
                              );
                            const helperText = formSubmitted
                              ? validateProfileFieldValueClient(
                                  field,
                                  displayValue,
                                )
                              : '';
                            return (
                              <Grid
                                size={getProfileFieldGridSize(field)}
                                key={`${cartIndex}-${index}`}
                              >
                                <ProfileFieldInput
                                  field={field}
                                  value={displayValue}
                                  onChange={handleProfileFieldChange(userId)}
                                  error={Boolean(helperText)}
                                  helperText={helperText}
                                />
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    ),
                )}
              </Box>
            ))
          ) : (
            <Grid size={{ xs: 12 }}>
              <p>No profile fields available</p>
            </Grid>
          )}
          <Grid size={{ xs: 12, md: 12 }}>
            <Box mt={2}>
              {hasPermission(
                rolePermissions,
                'Product Profile Info',
                'update',
              ) && (
                <Button
                  type='submit'
                  sx={{
                    position: 'relative',
                    minWidth: 100,
                    ml: 2.5,
                    backgroundColor: companyTheme
                      ? companyTheme.primary.main
                      : '#2196f3',
                    '&:hover': {
                      backgroundColor: companyTheme
                        ? alpha(companyTheme.primary.main, 0.8)
                        : alpha('#2196f3', 0.8),
                    },
                  }}
                  variant='contained'
                >
                  <IntlMessages id='common.save' />
                </Button>
              )}
              <Button
                sx={{
                  position: 'relative',
                  minWidth: 100,
                  ml: 2.5,
                  borderColor: companyTheme
                    ? companyTheme.primary.main
                    : '#2196f3',
                  '&:hover': {
                    borderColor: companyTheme
                      ? alpha(companyTheme.primary.main, 0.8)
                      : alpha('#2196f3', 0.8),
                  },
                  color: companyTheme ? companyTheme.primary.main : '#2196f3',
                }}
                variant='outlined'
                onClick={handleCancel}
                type='button'
              >
                <IntlMessages id='common.cancel' />
              </Button>
            </Box>
          </Grid>
        </form>
      </Box>
      <AppInfoView />
    </>
  );
};

export default ProfileData;
ProfileData.propTypes = {
  profileFields: PropTypes.array,
  companyTheme: PropTypes.object,
};
